import { useEffect, useRef, useState } from "react"
import readExcelFile from "read-excel-file/browser"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

// Upper bound on the rows rendered per sheet, header row included. A workbook
// with tens of thousands of rows would otherwise flood the DOM; users who need
// the whole thing use the Download button instead.
const MAX_PREVIEW_ROWS = 200

// How far a single click on the ‹ / › buttons nudges the tab strip.
const TAB_SCROLL_STEP = 160

type SheetPreview = {
  // Worksheet name as it appears in the workbook, used as the selector label
  sheet: string
  // Cell grid of the worksheet; the first row is rendered as the table header
  data: unknown[][]
}

type Props = {
  // Raw spreadsheet file to parse and display
  blob: Blob
  // Notifies the parent that parsing failed so it can offer a download instead
  onRenderFailed: () => void
}

// Date cells arrive as Date objects, which React refuses to render directly;
// empty cells arrive as null and must collapse to a blank cell.
const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return ""
  if (value instanceof Date) return value.toLocaleDateString()
  return String(value)
}

// Right-hand side of the footer bar. Returns null for an empty sheet, where the
// body already says "This sheet is empty."
const describeRowCount = (totalRows: number): string | null => {
  if (totalRows === 0) return null
  if (totalRows > MAX_PREVIEW_ROWS) {
    return `Showing the first ${MAX_PREVIEW_ROWS} of ${totalRows} rows — download the file for the rest.`
  }
  return `${totalRows} ${totalRows === 1 ? "row" : "rows"}`
}

type SheetTabsProps = {
  sheets: SheetPreview[]
  activeSheetIndex: number
  onSelect: (index: number) => void
}

// Excel-style tab strip. Tab labels stay plain text so the accessible name of
// each tab is exactly the worksheet name.
const SheetTabs = ({ sheets, activeSheetIndex, onSelect }: SheetTabsProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // The arrows only appear once the strip actually overflows, which depends on
  // the panel width — hence the ResizeObserver alongside the scroll listener.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const sync = () => {
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
      setCanScrollLeft(scroller.scrollLeft > 1)
      setCanScrollRight(scroller.scrollLeft < maxScrollLeft - 1)
    }

    sync()
    scroller.addEventListener("scroll", sync)
    // jsdom has no ResizeObserver; the scroll listener alone is enough there.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync)
    observer?.observe(scroller)

    return () => {
      scroller.removeEventListener("scroll", sync)
      observer?.disconnect()
    }
  }, [sheets])

  // Keep the selected tab reachable when the strip is scrolled elsewhere.
  useEffect(() => {
    const activeTab = scrollerRef.current?.querySelector<HTMLElement>(
      '[data-active="true"]',
    )
    activeTab?.scrollIntoView?.({ block: "nearest", inline: "nearest" })
  }, [activeSheetIndex])

  const scrollBy = (distance: number) =>
    scrollerRef.current?.scrollBy?.({ left: distance, behavior: "smooth" })

  const arrowClasses =
    "flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground"

  return (
    <>
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll sheet tabs left"
          onClick={() => scrollBy(-TAB_SCROLL_STEP)}
          className={arrowClasses}
        >
          <ChevronLeft className="size-4" />
        </button>
      )}

      <div
        ref={scrollerRef}
        role="tablist"
        className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sheets.map((sheet, index) => {
          const isActive = index === activeSheetIndex
          return (
            <button
              key={`${sheet.sheet}-${index}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-active={isActive}
              title={sheet.sheet}
              onClick={() => onSelect(index)}
              className={cn(
                "max-w-40 shrink-0 truncate rounded-b-md border-t-2 px-3 py-1 text-xs transition-colors",
                isActive
                  ? "border-t-primary bg-background font-semibold text-foreground shadow-sm"
                  : "border-t-transparent bg-muted text-muted-foreground hover:bg-background/70 hover:text-foreground",
              )}
            >
              {sheet.sheet}
            </button>
          )
        })}
      </div>

      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll sheet tabs right"
          onClick={() => scrollBy(TAB_SCROLL_STEP)}
          className={arrowClasses}
        >
          <ChevronRight className="size-4" />
        </button>
      )}
    </>
  )
}

export const SpreadsheetPreview = ({ blob, onRenderFailed }: Props) => {
  const [sheets, setSheets] = useState<SheetPreview[] | null>(null)
  const [activeSheetIndex, setActiveSheetIndex] = useState(0)

  // Parsing runs off the main thread inside read-excel-file's own Web Worker.
  // `cancelled` keeps a late resolution from touching an unmounted component.
  useEffect(() => {
    let cancelled = false
    readExcelFile(blob)
      .then((parsed) => {
        if (cancelled) return
        setSheets(parsed)
        setActiveSheetIndex(0)
      })
      .catch(() => {
        if (!cancelled) onRenderFailed()
      })
    return () => {
      cancelled = true
    }
    // `onRenderFailed` is intentionally excluded: the parent passes an inline
    // callback, so including it would re-parse the workbook on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob])

  if (sheets === null) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    )
  }

  const activeSheet = sheets[activeSheetIndex]
  const totalRows = activeSheet?.data.length ?? 0
  const visibleRows = activeSheet?.data.slice(0, MAX_PREVIEW_ROWS) ?? []
  const [headerRow, ...bodyRows] = visibleRows
  const rowCountLabel = describeRowCount(totalRows)
  const hasFooter = sheets.length > 1 || rowCountLabel !== null

  return (
    <div className="flex h-full flex-col">
      {totalRows === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
          This sheet is empty.
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                {headerRow.map((cell, columnIndex) => (
                  <th
                    key={columnIndex}
                    className="sticky top-0 border bg-muted px-2 py-1.5 text-left font-medium whitespace-nowrap"
                  >
                    {formatCellValue(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, columnIndex) => (
                    <td
                      key={columnIndex}
                      className="border px-2 py-1.5 align-top whitespace-nowrap"
                    >
                      {formatCellValue(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasFooter && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-muted/40 px-2 py-1.5">
          {sheets.length > 1 && (
            <SheetTabs
              sheets={sheets}
              activeSheetIndex={activeSheetIndex}
              onSelect={setActiveSheetIndex}
            />
          )}
          {rowCountLabel && (
            <p className="ml-auto shrink-0 pl-1 text-xs text-muted-foreground">
              {rowCountLabel}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
